import { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import teamService from '@/services/teamService.ts';
import type { TeamResponse } from '@/services/teamService.ts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import CustomSelect from '@/components/ui/CustomSelect.tsx';
import {
  createSalespersonSchema,
  type CreateSalespersonFormValues,
} from '@/schemas/createSalespersonSchema.ts';

interface Props {
  isOpen: boolean;
  teams: TeamResponse[];
  defaultTeamId?: string;
  onClose: () => void;
  onCreated: () => void;
}

export default function CreateSalespersonModal({ isOpen, teams, defaultTeamId, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const schema = useMemo(() => createSalespersonSchema(t), [t]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateSalespersonFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { teamId: defaultTeamId ?? teams[0]?.id ?? '' },
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      reset({ teamId: defaultTeamId ?? teams[0]?.id ?? '' });
      setError('');
      setShowPassword(false);
    }
  }, [isOpen, defaultTeamId, teams, reset]);

  const onSubmit = async (data: CreateSalespersonFormValues) => {
    setError('');
    setIsSubmitting(true);
    try {
      await teamService.createSalesperson({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone || undefined,
        teamId: data.teamId,
      });
      reset();
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('createSalesperson.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) { reset(); onClose(); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('createSalesperson.title')}</DialogTitle>
          <DialogDescription>{t('createSalesperson.subtitle')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('createSalesperson.nameLabel')}
              </label>
              <input
                {...register('firstName')}
                className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t('createSalesperson.namePlaceholder')}
              />
              {errors.firstName && (
                <p className="mt-1 text-xs text-destructive">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('createSalesperson.lastNameLabel')}
              </label>
              <input
                {...register('lastName')}
                className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t('createSalesperson.lastNamePlaceholder')}
              />
              {errors.lastName && (
                <p className="mt-1 text-xs text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t('createSalesperson.emailLabel')}
            </label>
            <input
              {...register('email')}
              type="email"
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={t('createSalesperson.emailPlaceholder')}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t('createSalesperson.phoneLabel')}
            </label>
            <input
              {...register('phone')}
              type="tel"
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={t('createSalesperson.phonePlaceholder')}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t('createSalesperson.passwordLabel')}
            </label>
            <div className="relative">
              <input
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                className="w-full rounded-lg border border-border bg-card px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder={t('createSalesperson.passwordPlaceholder')}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                tabIndex={-1}
                aria-label={showPassword ? t('common.hidePassword') : t('common.showPassword')}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </Button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-destructive">{errors.password.message}</p>
            )}
          </div>

          {teams.length > 1 && (
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('createSalesperson.teamLabel')}
              </label>
              <CustomSelect
                value={watch('teamId') ?? ''}
                onChange={(val) => setValue('teamId', val)}
                placeholder={t('createSalesperson.teamLabel')}
                options={teams.map((team) => ({ value: team.id, label: team.name }))}
              />
              {errors.teamId && (
                <p className="mt-1 text-xs text-destructive">{errors.teamId.message}</p>
              )}
            </div>
          )}
          {teams.length === 1 && (
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('createSalesperson.teamLabel')}
              </label>
              <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground">
                {teams[0].name}
              </p>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => { reset(); onClose(); }}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('createSalesperson.submitting') : t('createSalesperson.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
